import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { dateToTimestamp, timestampToDate } from 'src/utils/dateFormatter';
import { decamelize } from 'src/utils/decamelize';

import { normalize } from '../shared/normalizers';
import { User } from '../user/_types';
import { fetchUsers } from '../user/user';

import {
  Group,
  GroupCalendar,
  GroupExerciseLeaderboard,
  GroupLeaderboard,
  GroupMeasurementLeaderboard,
  GroupMemberHydrated,
  GroupMemberSearchResult,
  GroupMembersResponse,
  GroupMembership,
  GroupStats,
  GroupWodLeaderboard,
  NextScheduledPlan,
} from './_types';
import {
  denormalizeGroup,
  normalizeGroupMemberSearchResult,
  normalizeGroupSearchResult,
  normalizeMyGroups,
  normalizeNextScheduledPlan,
} from './normalizers';

export const fetchGroups = async ({
  sortBy,
  sortDir,
  page = 0,
  perPage = 20,
  search = '',
  isPublic = false,
}: {
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
  search?: string;
  isPublic?: boolean;
}): Promise<{ groups: Group[]; total: number }> => {
  try {
    const response = await api.get('/api/v2/groups', {
      params: {
        page,
        per: perPage,
        ...(search && { 'q[name_cont]': search }),
        ...(sortBy && {
          'q[s]': `${decamelize(sortBy || '')} ${sortDir || 'desc'}`,
        }),
        ...(isPublic && { public: true }),
      },
    });
    const normalizedGroups = response.data?.group?.map((group) =>
      normalize(group)
    );

    return { groups: normalizedGroups, total: response.data?.meta?.total };
  } catch (error) {
    throw new Error(api.formatError(error, 'Error fetching groups.'));
  }
};
export const saveGroup = async ({
  keepFields,
  ...group
}: Group & { keepFields?: string[] }): Promise<Group> => {
  const data = denormalizeGroup(group, keepFields);
  const response = group.id
    ? await api.put(`/api/v3/groups/${group.id}`, { group: data })
    : await api.post('/api/v3/groups', { group: data });
  return normalize(response.data);
};

export const handleFileUpload = async (
  file: File
): Promise<{ id: string; url: string }> => {
  const formData = new FormData();
  formData.append('asset_image[image]', file);
  const response = await api.post('/api/v2/assets/image/group', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const fetchClientGroups = async (
  userId: User['id'],
  groupId?: Group['id']
): Promise<GroupMembership[]> => {
  const params = {
    user_id: userId,
    fetch_all: true,
    ...(groupId && { group_id: groupId }),
  };

  const response = await api.get('/api/v3/group_members', {
    params,
  });

  return response.data.map((membership) => ({
    ...(normalize(membership) as GroupMembership),
    createdAt: timestampToDate(membership.created_at),
    updatedAt: timestampToDate(membership.updated_at),
  }));
};

export const fetchAllGroups = async (platformId?: string): Promise<Group[]> => {
  const response = await api.get('/api/v2/groups', {
    params: {
      fetch_all: true,
      with_stripe: true,
      'q[s]': 'name asc',
      ...(platformId && { platform_id: platformId }),
    },
  });

  return response.data.map(normalize);
};

export const fetchAllGroupsByPluckColumns = async (
  pluckColumns: string,
  platformId?: string
): Promise<Group[]> => {
  const response = await api.get('/api/v2/groups', {
    params: {
      fetch_all: true,
      pluck_columns: pluckColumns,
      'q[s]': 'name asc',
      ...(platformId && { platform_id: platformId }),
    },
  });

  return response.data.map(normalize);
};

interface FetchGroupOptions {
  id: Group['id'];
}

export const fetchGroup = async ({ id }: FetchGroupOptions): Promise<Group> => {
  const response = await api.get(`/api/v3/groups/${id}`);
  return normalize(response.data);
};

export const fetchGroupsByParams = async (params = {}): Promise<Group[]> => {
  const response = await api.get(`/api/v3/groups`, {
    params: { ...params },
  });
  return response.data.map(normalize);
};

export const fetchGroupCalendars = async (): Promise<GroupCalendar[]> => {
  const response = await api.get('/api/v3/groups/calendars');
  return response.data.map(normalize);
};

export const removeFromGroup = async (
  membershipId: GroupMembership['id']
): Promise<void> => {
  await api.delete(`/api/v3/group_members/${membershipId}`);
};

export const addToGroup = async (
  userId: User['id'],
  groupId: Group['id']
): Promise<GroupMembership> => {
  const response = await api.post('/api/v3/group_members', {
    user_id: userId,
    group_id: groupId,
  });

  return normalize(response.data);
};

export const cloneGroup = async (groupId: Group['id']): Promise<Group> => {
  return await api.get(`/api/v2/groups/${groupId}/clone`);
};

export const deleteGroup = async (groupId: Group['id']): Promise<void> => {
  return await api.delete(`/api/v3/groups/${groupId}`);
};
export const fetchUserGroups = async (params: {
  for_user?: boolean;
  member_only?: boolean;
  ids?: number[];
}): Promise<Group[]> => {
  try {
    const response = await api.get('/api/v2/groups', {
      params: {
        fetch_all: true,
        ...params,
      },
    });
    return response.data.map(normalizeMyGroups);
  } catch (error) {
    throw new Error(api.formatError(error, 'Error fetching user groups.'));
  }
};

export const searchGroupResults = async ({
  search,
  trainerId,
  signal,
}: {
  search: string;
  trainerId: Identifier;
  signal?: AbortSignal;
}): Promise<GroupMemberSearchResult[]> => {
  const response = await api.get('/sm/search', {
    signal,
    params: {
      types: [`group_white_label_${trainerId}`],
      limit: 20,
      no_limit: false,
      term: search,
      page: 1,
    },
  });
  return response.data.results[`group_white_label_${trainerId}`].map(
    (r: GroupMemberSearchResult) => normalizeGroupSearchResult(r)
  );
};

export const fetchGroupLeaderboard = async ({
  groupId,
  startDate,
  endDate,
  page,
  per,
  sort,
  userId,
}: {
  groupId: Group['id'];
  startDate?: string;
  endDate?: string;
  page?: number;
  per?: number;
  sort?: string;
  userId?: string;
}): Promise<GroupLeaderboard[]> => {
  const response = await api.get(`/api/v3/groups/${groupId}/leaderboard`, {
    params: {
      start_date: startDate ? dateToTimestamp(new Date(startDate)) : undefined,
      end_date: endDate ? dateToTimestamp(new Date(endDate)) : undefined,
      page,
      per,
      sort,
      user_id: userId,
    },
  });
  return normalize(response.data.leaderboard);
};

export interface GroupMeasurementLeaderboardResponse {
  leaderboard: GroupMeasurementLeaderboard[];
  total: number;
}

export const fetchGroupMeasurementLeaderboard = async ({
  groupId,
  startDate,
  endDate,
  metric,
  page,
  per,
  sort,
}: {
  groupId: Group['id'];
  startDate?: string;
  endDate?: string;
  metric: string;
  page?: number;
  per?: number;
  sort?: string;
}): Promise<GroupMeasurementLeaderboardResponse> => {
  const response = await api.get(
    `/api/v3/groups/${groupId}/measurement_leaderboard/`,
    {
      params: {
        start_date: startDate
          ? dateToTimestamp(new Date(startDate))
          : undefined,
        end_date: endDate
          ? dateToTimestamp(new Date(endDate))
          : undefined,
        stat: metric,
        page,
        per,
        sort,
      },
    }
  );

  return {
    leaderboard: normalize(response.data.leaderboard || []) || [],
    total: response.data.meta.total || 0,
  };
};

export interface GroupWodLeaderboardResponse {
  leaderboard: GroupWodLeaderboard[];
  total: number;
}

export const fetchGroupWodLeaderboard = async ({
  groupId,
  startDate,
  gender,
  page,
  per,
}: {
  groupId: Group['id'];
  startDate?: string;
  gender?: string | null;
  page?: number;
  per?: number;
}): Promise<GroupWodLeaderboardResponse> => {
  const response = await api.get(`/api/v3/groups/${groupId}/wod_leaderboard/`, {
    params: {
      start_date: startDate ? dateToTimestamp(new Date(startDate)) : undefined,
      gender,
      page,
      per,
    },
  });

  return {
    leaderboard: normalize(response.data.leaderboard || []) || [],
    total: response.data.meta.total || 0,
  };
};

export interface GroupExerciseLeaderboardResponse {
  leaderboard: GroupExerciseLeaderboard[];
  total: number;
}

export const fetchGroupExerciseLeaderboard = async ({
  groupId,
  startDate,
  endDate,
  page,
  per,
  sort,
  exerciseId,
}: {
  groupId: Group['id'];
  startDate?: string;
  endDate?: string;
  page?: number;
  per?: number;
  sort?: string;
  exerciseId: string;
}): Promise<GroupExerciseLeaderboardResponse> => {
  const response = await api.get(
    `/api/v3/groups/${groupId}/exercise_leaderboard/`,
    {
      params: {
        start_date: startDate
          ? dateToTimestamp(new Date(startDate))
          : undefined,
        end_date: endDate ? dateToTimestamp(new Date(endDate)) : undefined,
        page,
        per,
        sort,
        exercise_id: exerciseId,
      },
    }
  );

  return {
    leaderboard: normalize(response.data.leaderboard || []) || [],
    total: response.data.total || 0,
  };
};

interface searchGroupMembersOptions {
  search: string;
  types: string[];
  noLimit?: boolean;
  signal?: AbortSignal;
}

export const searchGroupMembers = async ({
  search,
  types = [],
  noLimit = false,
  signal,
}: searchGroupMembersOptions) => {
  const response = await api.get('/api/v4/autocomplete', {
    signal,
    params: {
      types,
      limit: 10,
      no_limit: noLimit,
      term: search,
      page: 1,
    },
  });
  return response.data.results[types[0]].map((r) =>
    normalizeGroupMemberSearchResult(r)
  );
};

// Fetch group stats (total weight, distance, calories)
export const fetchGroupStats = async (
  groupId: Group['id']
): Promise<GroupStats> => {
  const response = await api.get(`/api/v4/groups/${groupId}/stats`);
  return normalize(response.data);
};

// Join a free group
export const joinGroup = async (groupId: Group['id']): Promise<void> => {
  await api.get(`/groups/${groupId}/join`);
};

// Leave a group (for free groups or to initiate cancellation)
export const leaveGroup = async (
  membershipId: GroupMembership['id']
): Promise<void> => {
  await api.delete(`/api/v3/group_members/${membershipId}`);
};

// Dismiss the welcome modal for a group membership
export const dismissGroupWelcome = async (
  groupId: Group['id']
): Promise<void> => {
  await api.post(`/api/v4/groups/${groupId}/dismiss_welcome`);
};

// Fetch the next scheduled workout plan for the current user in a group
export const fetchNextScheduledPlan = async (
  groupId: Group['id']
): Promise<NextScheduledPlan> => {
  const response = await api.get(`/api/v4/groups/${groupId}/next_scheduled_plan`);
  return normalizeNextScheduledPlan(response.data);
};

// ============================================
// Group Members API Functions
// ============================================

export interface FetchGroupMembersParams {
  groupId: Identifier;
  page?: number;
  perPage?: number;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

/**
 * Fetch members of a specific group (admin only)
 * Hydrates members with user data using fetchUsers with_ids
 */
export const fetchGroupMembers = async ({
  groupId,
  page = 1,
  perPage = 25,
  search,
  sortBy,
  sortDir,
}: FetchGroupMembersParams): Promise<GroupMembersResponse> => {
  const response = await api.get('/api/v3/group_members', {
    params: {
      group_id: groupId,
      page,
      per: perPage,
      ...(search && { 'q[user_formatted_name_or_user_email_cont]': search }),
      ...(sortBy && {
        'q[s]': `${decamelize(sortBy)} ${sortDir || 'desc'}`,
      }),
    },
  });

  const memberships = (response.data.group_member || []).map((member: unknown) => ({
    ...(normalize(member) as GroupMembership),
    createdAt: timestampToDate((member as Record<string, unknown>).created_at as number),
    updatedAt: timestampToDate((member as Record<string, unknown>).updated_at as number),
  })) as GroupMembership[];

  const total = parseInt(response.data.meta.total || '0', 10);

  // Fetch user data for all members
  const userIds = memberships.map((m) => m.userId).filter(Boolean);
  let usersMap: Record<Identifier, User> = {};

  if (userIds.length > 0) {
    const users = await fetchUsers({ ids: userIds });
    usersMap = users.reduce(
      (acc, user) => {
        acc[user.id] = user;
        return acc;
      },
      {} as Record<Identifier, User>
    );
  }

  // Hydrate members with user data
  const members: GroupMemberHydrated[] = memberships.map((membership) => {
    const user = usersMap[membership.userId];
    return {
      ...membership,
      user: user
        ? {
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            nickname: user.nickname,
            email: user.email,
            profileImage: user.profileImage,
          }
        : null,
    };
  });

  return { members, total };
};

/**
 * Update a group member (admin status, join date, etc.)
 */
export const updateGroupMember = async (
  memberId: Identifier,
  updates: { admin?: boolean; createdAt?: Date }
): Promise<GroupMembership> => {
  const data: Record<string, unknown> = {};
  if (updates.admin !== undefined) {
    data.admin = updates.admin;
  }
  if (updates.createdAt !== undefined) {
    // Convert to unix timestamp (seconds) as expected by the API
    data.created_at = updates.createdAt instanceof Date
      ? Math.floor(updates.createdAt.getTime() / 1000)
      : updates.createdAt;
  }

  const response = await api.put(`/api/v3/group_members/${memberId}`, {
    group_member: data,
  });
  return {
    ...(normalize(response.data) as GroupMembership),
    createdAt: timestampToDate(response.data.created_at),
    updatedAt: timestampToDate(response.data.updated_at),
  };
};

/**
 * Toggle admin status for a group member
 */
export const toggleGroupMemberAdmin = async (
  memberId: Identifier,
  admin: boolean
): Promise<GroupMembership> => {
  return updateGroupMember(memberId, { admin });
};

/**
 * Update a group member's join date
 */
export const updateGroupMemberJoinDate = async (
  memberId: Identifier,
  createdAt: Date
): Promise<GroupMembership> => {
  return updateGroupMember(memberId, { createdAt });
};

export interface RemoveGroupMemberParams {
  memberId: Identifier;
  endOfBillingPeriod?: boolean;
  disableEmails?: boolean;
  reason?: string;
  additionalInfo?: string;
}

/**
 * Remove a member from a group
 * @param memberId - The group member ID
 * @param endOfBillingPeriod - If true, cancel at end of billing period instead of immediately
 * @param disableEmails - If true, don't send cancellation emails
 * @param reason - Optional reason for cancellation
 * @param additionalInfo - Optional additional information
 */
export const removeGroupMember = async ({
  memberId,
  endOfBillingPeriod = false,
  disableEmails = false,
  reason,
  additionalInfo,
}: RemoveGroupMemberParams): Promise<void> => {
  await api.delete(`/api/v3/group_members/${memberId}`, {
    params: {
      ...(endOfBillingPeriod && { end_of_billing: true }),
      ...(disableEmails && { disable_emails: true }),
      ...(reason && { reason_for_cancellation: reason }),
      ...(additionalInfo && { additional_information: additionalInfo }),
    },
  });
};

/**
 * Follow group calendar for a member
 */
export const followGroupCalendar = async (
  memberId: Identifier
): Promise<GroupMembership> => {
  const response = await api.post(`/api/v3/group_members/${memberId}/follow_calendar`);
  return normalize(response.data);
};

/**
 * Unfollow group calendar for a member
 */
export const unfollowGroupCalendar = async (
  memberId: Identifier
): Promise<GroupMembership> => {
  const response = await api.post(`/api/v3/group_members/${memberId}/unfollow_calendar`);
  return normalize(response.data);
};

/**
 * Export group member information
 * Triggers an async export job - the user will be notified when complete
 */
export const exportGroupMemberInfo = async (
  groupId: Group['id']
): Promise<{ success: boolean; message: string }> => {
  const response = await api.get(`/api/v2/groups/${groupId}/export_member_info`);
  return response.data;
};

/**
 * Export group assessment completion information
 * Triggers an async export job - the user will be notified when complete
 */
export const exportGroupAssessmentInfo = async (
  groupId: Group['id']
): Promise<{ success: boolean; message: string }> => {
  const response = await api.get(`/api/v2/groups/${groupId}/export_assessment_info`);
  return response.data;
};

// ============================================
// Group Calendar Event API Functions
// ============================================

export interface GroupCalendarEvent {
  objectId: Identifier;
  objectType: 'workout' | 'logged_workout';
  text: string;
  date: number;
  ownerId: Identifier;
  data: {
    text: string;
    color?: string;
    trainerName?: string;
    typeId?: Identifier;
    workoutPlanId?: Identifier;
  };
}

/**
 * Fetch calendar events for a group
 * Returns workouts and logged workouts for the specified date range
 */
export const fetchGroupCalendarEvents = async ({
  groupId,
  start,
  end,
}: {
  groupId: Identifier;
  start: number;
  end: number;
}): Promise<GroupCalendarEvent[]> => {
  const response = await api.get('/api/v4/calendar', {
    params: {
      group_id: groupId,
      start,
      end,
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      object_type: 'workout,logged_workout',
    },
  });
  return response.data.map((event: Record<string, unknown>) => normalize(event));
};

export interface UserPlan {
  id: Identifier;
  endDate: string | null;
  workoutPlanId: Identifier;
}

/**
 * Fetch user plan from workout plan ID
 * Used to determine if user is following a group calendar
 */
export const fetchUserPlanFromWorkoutPlan = async (
  workoutPlanId: Identifier
): Promise<UserPlan | null> => {
  try {
    const response = await api.get('/api/v2/user_plans/user_plan_from_workout_plan', {
      params: { workout_plan_id: workoutPlanId },
    });
    if (!response.data || !response.data.id) {
      return null;
    }
    return normalize(response.data);
  } catch {
    return null;
  }
};

/**
 * Toggle follow/unfollow calendar for a group
 * Returns the updated user plan or null if unfollowed
 */
export const toggleFollowGroupCalendar = async (
  groupId: Identifier
): Promise<UserPlan | null> => {
  const response = await api.get('/api/v2/user_plans/toggle_follow_calendar', {
    params: { group_id: groupId },
  });
  if (!response.data || response.data.end_date) {
    return null;
  }
  return normalize(response.data);
};
